import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Coins, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AgentPhotoToolsModal from "@/components/AgentPhotoToolsModal";
import { CreditPurchaseReturnWatcher } from "@/components/CreditsTopUpModal";

export function ShareEditBanner({ right }) {
    return (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4 px-4 py-3 bg-[#E5A93D]/[0.07] border border-[#E5A93D]/30 rounded-sm" data-testid="share-edit-banner">
            <div className="flex items-center gap-2 text-sm text-zinc-800">
                <Sparkles className="h-4 w-4 text-[#E5A93D] shrink-0" />
                <span>
                    <span className="font-bold">Bewerk de foto's met AI</span> — klik op een foto en kies daarna{" "}
                    <span className="font-bold text-[#B8860B]">'Bewerk met AI'</span>.
                </span>
            </div>
            {right}
        </div>
    );
}

// Gedeelde logica voor AI-bewerken op publieke deellinks (fotoreeks +
// totaalproject). Deellink-token + account = bewerkrechten (claim-access
// maakt de gebruiker zo nodig co-agent). Anoniem → login/registreer-popup.
export function useShareAIEdit({ token, photos, refetch, enabled = true, onBeforeOpen }) {
    const { user, login, register } = useAuth();
    const isAuthed = !!user && typeof user === "object";
    const isAdmin = user?.role === "admin";
    const [canEdit, setCanEdit] = useState(false);
    const [reqId, setReqId] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorIdx, setEditorIdx] = useState(null);
    const [loginOpen, setLoginOpen] = useState(false);
    const [authMode, setAuthMode] = useState("login"); // "login" | "register"
    const [pendingIdx, setPendingIdx] = useState(null);
    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");
    const [regName, setRegName] = useState("");
    const [regCompany, setRegCompany] = useState("");
    const [busy, setBusy] = useState(false);

    const claimAccess = async () => {
        const { data } = await api.post(`/public/preview/${token}/claim-access`);
        setReqId(data.request_id);
        setCanEdit(true);
        return data.request_id;
    };

    useEffect(() => {
        if (!enabled || !isAuthed || !token) { setCanEdit(false); return undefined; }
        let cancelled = false;
        api.post(`/public/preview/${token}/claim-access`)
            .then(({ data: d }) => { if (!cancelled) { setReqId(d.request_id); setCanEdit(true); } })
            .catch(() => { if (!cancelled) setCanEdit(false); });
        return () => { cancelled = true; };
    }, [enabled, isAuthed, token]);

    const openEditor = (i) => {
        onBeforeOpen?.();
        setEditorIdx(i);
        setEditorOpen(true);
    };

    const handleEditClick = async (i) => {
        if (!enabled) return;
        if (!isAuthed) { setPendingIdx(i); setAuthMode("login"); setLoginOpen(true); return; }
        if (!canEdit) {
            try { await claimAccess(); } catch { toast.error("Kon geen bewerktoegang krijgen voor dit project"); return; }
        }
        openEditor(i);
    };

    const afterAuthSuccess = async () => {
        setLoginOpen(false);
        setLoginPassword("");
        if (pendingIdx !== null) {
            try {
                await claimAccess();
                openEditor(pendingIdx);
            } catch {
                toast.error("Kon geen bewerktoegang krijgen voor dit project");
            }
            setPendingIdx(null);
        }
    };

    const submitLogin = async (e) => {
        e.preventDefault();
        if (!loginEmail.trim() || !loginPassword) return;
        setBusy(true);
        const res = await login(loginEmail.trim(), loginPassword);
        if (!res.ok) {
            toast.error(res.error || "Inloggen mislukt");
            setBusy(false);
            return;
        }
        await afterAuthSuccess();
        setBusy(false);
    };

    const submitRegister = async (e) => {
        e.preventDefault();
        if (!regName.trim() || !loginEmail.trim() || !loginPassword) return;
        if (loginPassword.length < 6) { toast.error("Wachtwoord: minstens 6 tekens"); return; }
        setBusy(true);
        const res = await register({
            name: regName.trim(),
            email: loginEmail.trim(),
            password: loginPassword,
            company: regCompany.trim() || undefined,
            account_type: "agent",
        });
        if (!res.ok) {
            toast.error(res.error || "Account aanmaken mislukt");
            setBusy(false);
            return;
        }
        toast.success("Account aangemaakt — 10 gratis credits staan klaar ✨");
        await afterAuthSuccess();
        setBusy(false);
    };

    const creditsBadge = enabled && isAuthed && canEdit ? (
        <span
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[#E5A93D] bg-[#E5A93D]/10 border border-[#E5A93D]/30 rounded-sm px-3 h-8"
            data-testid="share-credits-badge"
            title="Jouw beschikbare credits voor AI-bewerkingen"
        >
            <Coins className="h-3.5 w-3.5" /> {isAdmin ? "∞" : (user?.credits_balance ?? 0)} credits
        </span>
    ) : null;

    const modals = (
        <>
            <CreditPurchaseReturnWatcher />
            {loginOpen && (
                <div
                    className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setLoginOpen(false)}
                    data-testid="share-login-modal"
                >
                    <div className="bg-white rounded-md w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="h-4 w-4 text-[#E5A93D]" />
                            <h3 className="font-display font-bold text-lg text-zinc-950">
                                {authMode === "login" ? "Meld je aan om te bewerken" : "Maak je gratis account"}
                            </h3>
                        </div>
                        <p className="text-sm text-zinc-500 mb-4">
                            {authMode === "login"
                                ? "Log in met je account — daarna bewerk je foto's rechtstreeks hier met je eigen credits."
                                : "Nieuw account = 10 gratis credits. Bewerk daarna meteen foto's in deze reeks."}
                        </p>
                        <div className="grid grid-cols-2 gap-1 bg-zinc-100 rounded-sm p-1 mb-4">
                            <button
                                type="button"
                                onClick={() => setAuthMode("login")}
                                className={`h-8 text-xs font-bold rounded-sm transition ${authMode === "login" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
                                data-testid="share-auth-tab-login"
                            >
                                Inloggen
                            </button>
                            <button
                                type="button"
                                onClick={() => setAuthMode("register")}
                                className={`h-8 text-xs font-bold rounded-sm transition ${authMode === "register" ? "bg-white text-zinc-950 shadow-sm" : "text-zinc-500 hover:text-zinc-800"}`}
                                data-testid="share-auth-tab-register"
                            >
                                Account aanmaken
                            </button>
                        </div>
                        <form onSubmit={authMode === "login" ? submitLogin : submitRegister} className="space-y-3">
                            {authMode === "register" && (
                                <>
                                    <Input
                                        type="text"
                                        required
                                        placeholder="Je naam"
                                        value={regName}
                                        onChange={(e) => setRegName(e.target.value)}
                                        data-testid="share-register-name"
                                    />
                                    <Input
                                        type="text"
                                        placeholder="Kantoor (optioneel)"
                                        value={regCompany}
                                        onChange={(e) => setRegCompany(e.target.value)}
                                        data-testid="share-register-company"
                                    />
                                </>
                            )}
                            <Input
                                type="email"
                                required
                                autoFocus={authMode === "login"}
                                placeholder="E-mailadres"
                                value={loginEmail}
                                onChange={(e) => setLoginEmail(e.target.value)}
                                data-testid="share-login-email"
                            />
                            <Input
                                type="password"
                                required
                                placeholder={authMode === "login" ? "Wachtwoord" : "Kies een wachtwoord (min. 6 tekens)"}
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                data-testid="share-login-password"
                            />
                            <div className="flex gap-2 pt-1">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setLoginOpen(false)} data-testid="share-login-cancel">
                                    Annuleren
                                </Button>
                                <Button type="submit" disabled={busy} className="flex-1 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold" data-testid="share-login-submit">
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : authMode === "login" ? "Inloggen" : "Start met 10 credits"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {reqId && (
                <AgentPhotoToolsModal
                    open={editorOpen}
                    onClose={() => { setEditorOpen(false); setEditorIdx(null); refetch?.(); }}
                    requestId={reqId}
                    photos={photos || []}
                    initialIndex={editorIdx}
                    onRefetch={refetch}
                />
            )}
        </>
    );

    return { handleEditClick, creditsBadge, modals, isAuthed, canEdit };
}
